import React from 'react'

import { Box, IconButton, Stack, Typography } from '@mui/material'
import { PlusIcon, UserRoundIcon, XIcon } from 'lucide-react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { Button, FieldLabel } from '@repo/ui'
import { InfoTooltip, ScanMediaField, SelectField, TextField } from 'components'
import { MAX_LENGTH } from 'constants/messages'

/**
 * VRG-720 — the Contact card section of the product form.
 *
 * Seven plain fields (photo, name, title, company, phone, email, website) plus up to eight social
 * links. The values live under `contact_card.*` in the page's form, so the page's single
 * `reset(record)` baseline, dirty gate (§54) and payload builder own them like every other field;
 * this component is presentation and per-field validation only.
 *
 * Reuses the shared fields on purpose (§28): `TextField` + `SelectField` for the rows, and the
 * existing `ScanMediaField` upload for the portrait (same endpoint, same preview), restricted to
 * the formats a phone's contacts app can embed.
 */

export const CONTACT_CARD_MAX_SOCIALS = 8

export type SocialNetwork = 'linkedin' | 'instagram' | 'x' | 'tiktok' | 'facebook' | 'youtube' | 'other'

export const SOCIAL_NETWORK_OPTIONS: { value: SocialNetwork; label: string; placeholder: string }[] = [
  { value: 'linkedin', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/in/your-name' },
  { value: 'instagram', label: 'Instagram', placeholder: 'https://www.instagram.com/your-name' },
  { value: 'x', label: 'X', placeholder: 'https://x.com/your-name' },
  { value: 'tiktok', label: 'TikTok', placeholder: 'https://www.tiktok.com/@your-name' },
  { value: 'facebook', label: 'Facebook', placeholder: 'https://www.facebook.com/your-name' },
  { value: 'youtube', label: 'YouTube', placeholder: 'https://www.youtube.com/@your-name' },
  { value: 'other', label: 'Other', placeholder: 'https://' },
]

/** Hosts a known network's link may point at. Mirrors the API's check (loose, not an identity proof). */
const SOCIAL_HOSTS: Record<Exclude<SocialNetwork, 'other'>, string[]> = {
  linkedin: ['linkedin.com', 'lnkd.in'],
  instagram: ['instagram.com'],
  x: ['x.com', 'twitter.com'],
  tiktok: ['tiktok.com'],
  facebook: ['facebook.com', 'fb.com', 'fb.me'],
  youtube: ['youtube.com', 'youtu.be'],
}

const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/
const PHONE_RE = /^[+0-9][0-9 ()\-.]{3,}$/

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value)
  } catch {
    return null
  }
}

/** Field rules, passed to the Controller (never to TextField, which would register twice). */
export const CONTACT_CARD_RULES = {
  name: { maxLength: { value: 120, message: MAX_LENGTH('Name', 120) } },
  title: { maxLength: { value: 120, message: MAX_LENGTH('Title', 120) } },
  company: { maxLength: { value: 120, message: MAX_LENGTH('Company', 120) } },
  phone: {
    maxLength: { value: 40, message: MAX_LENGTH('Phone', 40) },
    validate: (v?: string | null) =>
      !v?.trim() || PHONE_RE.test(v.trim()) || 'Enter a valid phone number.',
  },
  email: {
    maxLength: { value: 254, message: MAX_LENGTH('Email', 254) },
    validate: (v?: string | null) =>
      !v?.trim() || EMAIL_RE.test(v.trim()) || 'Enter a valid email address.',
  },
  website: {
    maxLength: { value: 2048, message: MAX_LENGTH('Website', 2048) },
    validate: (v?: string | null) => {
      if (!v?.trim()) return true
      const url = parseUrl(v.trim())
      return (url && (url.protocol === 'https:' || url.protocol === 'http:')) ||
        'Enter a full web address starting with https://'
    },
  },
} as const

const socialUrlRule = (network: SocialNetwork) => (v?: string | null) => {
  if (!v?.trim()) return true
  const url = parseUrl(v.trim())
  if (!url || url.protocol !== 'https:') return 'Enter a full web address starting with https://'
  if (network !== 'other') {
    const host = url.hostname.toLowerCase()
    const ok = SOCIAL_HOSTS[network].some((d) => host === d || host.endsWith(`.${d}`))
    if (!ok) {
      const label = SOCIAL_NETWORK_OPTIONS.find((o) => o.value === network)?.label ?? network
      return `That does not look like a ${label} link.`
    }
  }
  return true
}

// Narrower than the media uploader on purpose. The API re-encodes whatever it is handed to a
// JPEG thumbnail (`prepareVCardPhoto`), so it is no longer the embeddable set that limits this —
// it is that a contact portrait is a photo, and offering video or an SVG here would only invite
// an upload the download then silently refuses. Size is the shared 8MB cap, which the API's
// fetch cap is pinned to (contact-card-photo-embed.guard.spec.ts).
const PHOTO_ACCEPT: Record<string, string[]> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
}

// The dropzone wants `ComponentType<{ size?: number; strokeWidth?: number }>`, which lucide's own
// `LucideIcon` (size: string | number) is not assignable to, so adapt it once here.
const PhotoPromptIcon = (props: { size?: number; strokeWidth?: number }) => (
  <UserRoundIcon {...props} />
)

const twoUp = { flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 240 }

export const ContactCardFields = () => {
  const methods = useFormContext()
  const { control } = methods
  const { fields, append, remove } = useFieldArray({ control, name: 'contact_card.socials' })

  const textRow = (
    name: 'name' | 'title' | 'company' | 'phone' | 'email' | 'website',
    label: string,
    placeholder: string,
    extra: { type?: string } = {},
  ) => (
    <Box sx={twoUp}>
      <Controller
        name={`contact_card.${name}`}
        control={control}
        rules={CONTACT_CARD_RULES[name]}
        // Same single-registration wiring as `title` on this page: no `{...field}` spread and no
        // `rules` on TextField (it would register the field a second time).
        render={({ field, fieldState }) => (
          <TextField
            {...methods}
            label={label}
            name={`contact_card.${name}`}
            placeholder={placeholder}
            type={extra.type}
            value={field.value == null ? '' : String(field.value)}
            onValueChange={(value) => field.onChange(value)}
            onFieldBlur={field.onBlur}
            helperText={fieldState.error?.message ?? 'optional'}
          />
        )}
      />
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        A person's details that people who can see this item's certificate can save to their phone.
        Built for business cards. Leave everything blank for no contact card.{' '}
        <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
          <InfoTooltip
            term="contact-card"
            text="Shown in a Contact section on the certificate page, to people who have access to it, with a Save contact button that adds it (photo and social links included) to their phone. Where an email is requested first, they see it after sharing theirs."
            doc="products/products#contact-card"
          />
        </Box>
      </Typography>

      <Controller
        name="contact_card.photo_url"
        control={control}
        render={({ field }) => (
          <ScanMediaField
            label="Photo"
            value={field.value}
            onChange={(url) => field.onChange(url)}
            accept={PHOTO_ACCEPT}
            description="JPG or PNG"
            alt="Contact photo"
            icon={PhotoPromptIcon}
            galleryTitle="Contact photo"
          />
        )}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        {textRow('name', 'Name', 'Spencer Stebbins')}
        {textRow('title', 'Title', 'Founder')}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        {textRow('company', 'Company', 'Vera Inc.')}
        {textRow('website', 'Website', 'https://vera.inc')}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        {textRow('phone', 'Phone', '+1 305 555 0100', { type: 'tel' })}
        {textRow('email', 'Email', 'name@company.com', { type: 'email' })}
      </Box>

      <Box>
        <FieldLabel label="Social links" adornment="(optional)" />
        <Stack spacing={3}>
          {fields.map((row, index) => {
            const network = (methods.watch(`contact_card.socials.${index}.network`) ||
              'linkedin') as SocialNetwork
            const placeholder =
              SOCIAL_NETWORK_OPTIONS.find((o) => o.value === network)?.placeholder ?? 'https://'
            return (
              <Box key={row.id} sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <Box sx={{ width: 160, flexShrink: 0 }}>
                  <Controller
                    name={`contact_card.socials.${index}.network`}
                    control={control}
                    render={({ field }) => (
                      <SelectField
                        options={SOCIAL_NETWORK_OPTIONS.map(({ value, label }) => ({ value, label }))}
                        value={field.value || 'linkedin'}
                        onChange={(value) => {
                          field.onChange(value)
                          // The URL's rule depends on the network, so re-check it on a change.
                          void methods.trigger(`contact_card.socials.${index}.url`)
                        }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Controller
                    name={`contact_card.socials.${index}.url`}
                    control={control}
                    rules={{
                      validate: (v?: string | null) =>
                        socialUrlRule(
                          (methods.getValues(`contact_card.socials.${index}.network`) ||
                            'linkedin') as SocialNetwork,
                        )(v),
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...methods}
                        name={`contact_card.socials.${index}.url`}
                        placeholder={placeholder}
                        value={field.value == null ? '' : String(field.value)}
                        onValueChange={(value) => field.onChange(value)}
                        onFieldBlur={field.onBlur}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Box>
                {/* X, not a trash can (§38): removing a row from the card's set; nothing is
                    destroyed and re-adding it is cheap. Sits at the field's 49px height. */}
                <IconButton
                  aria-label="Remove this social link"
                  onClick={() => remove(index)}
                  sx={{ width: 49, height: 49, borderRadius: '8px', color: 'text.secondary' }}
                >
                  <XIcon size={18} strokeWidth={1} />
                </IconButton>
              </Box>
            )
          })}

          {fields.length < CONTACT_CARD_MAX_SOCIALS && (
            <Box>
              <Button
                variant="secondary"
                disableElevation
                startIcon={<PlusIcon strokeWidth={1} />}
                onClick={() => {
                  // Start on the first network not already on the card, so a second row does
                  // not open as a duplicate LinkedIn that has to be changed by hand.
                  const used = new Set(
                    (methods.getValues('contact_card.socials') ?? []).map(
                      (s: { network?: string }) => s?.network,
                    ),
                  )
                  const next =
                    SOCIAL_NETWORK_OPTIONS.find((o) => o.value !== 'other' && !used.has(o.value))
                      ?.value ?? 'other'
                  append({ network: next, url: '' })
                }}
              >
                Add social link
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
